import { useState, useCallback } from "react";
import {
  FormLayout,
  TextField,
  Select,
  BlockStack,
  Text,
  Banner,
  InlineStack,
  Box,
} from "@shopify/polaris";
import { useTranslation } from "../hooks/useTranslation";

export interface ContractFormData {
  company_official_name: string;
  company_short_name: string;
  issuer_vat_number: string;
  issuer_address: string;
  issuer_city: string;
  issuer_postal_code: string;
  tax_office: string;
  profession: string;
  legal_representative_name: string;
  administrator_personal_vat_id: string;
  contact_email: string;
  phone: string;
  service_type: "b2b" | "b2c" | "b2b_and_b2c";
  alt_provider_name: string;
  alt_provider_contract_nr: string;
  alt_provider_contract_date: string;
}

export const defaultContractFormData: ContractFormData = {
  company_official_name: "",
  company_short_name: "",
  issuer_vat_number: "",
  issuer_address: "",
  issuer_city: "",
  issuer_postal_code: "",
  tax_office: "",
  profession: "",
  legal_representative_name: "",
  administrator_personal_vat_id: "",
  contact_email: "",
  phone: "",
  service_type: "b2b",
  alt_provider_name: "",
  alt_provider_contract_nr: "",
  alt_provider_contract_date: "",
};

interface ContractFormProps {
  data: ContractFormData;
  onChange: (data: ContractFormData) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

const SERVICE_TYPE_OPTIONS_KEYS = [
  { key: "contractForm.fields.serviceTypes.b2b", value: "b2b" },
  { key: "contractForm.fields.serviceTypes.b2c", value: "b2c" },
  { key: "contractForm.fields.serviceTypes.b2b_and_b2c", value: "b2b_and_b2c" },
];

export default function ContractForm({
  data,
  onChange,
  errors = {},
  disabled = false,
}: ContractFormProps) {
  const { t } = useTranslation();

  const SERVICE_TYPE_OPTIONS = SERVICE_TYPE_OPTIONS_KEYS.map(({ key, value }) => ({
    label: t(key),
    value,
  }));

  const needsAltProvider =
    data.service_type === "b2c" || data.service_type === "b2b_and_b2c";

  const handleChange = useCallback(
    (field: keyof ContractFormData) => (value: string) => {
      onChange({ ...data, [field]: value });
    },
    [data, onChange],
  );

  return (
    <BlockStack gap="400">
      {/* Company Details */}
      <Text as="h3" variant="headingMd">
        {t("contractForm.section.companyDetails")}
      </Text>
      <FormLayout>
        <FormLayout.Group>
          <TextField
            label={t("contractForm.fields.officialName")}
            value={data.company_official_name}
            onChange={handleChange("company_official_name")}
            error={errors.company_official_name}
            disabled={disabled}
            autoComplete="organization"
            requiredIndicator
          />
          <TextField
            label={t("contractForm.fields.tradingName")}
            value={data.company_short_name}
            onChange={handleChange("company_short_name")}
            error={errors.company_short_name}
            disabled={disabled}
            autoComplete="off"
            requiredIndicator
          />
        </FormLayout.Group>

        <FormLayout.Group>
          <TextField
            label={t("contractForm.fields.vatNumber")}
            value={data.issuer_vat_number}
            onChange={handleChange("issuer_vat_number")}
            error={errors.issuer_vat_number}
            disabled={disabled}
            autoComplete="off"
            requiredIndicator
            maxLength={9}
          />
          <TextField
            label={t("contractForm.fields.taxOffice")}
            value={data.tax_office}
            onChange={handleChange("tax_office")}
            error={errors.tax_office}
            disabled={disabled}
            autoComplete="off"
            requiredIndicator
          />
        </FormLayout.Group>

        <TextField
          label={t("contractForm.fields.profession")}
          value={data.profession}
          onChange={handleChange("profession")}
          error={errors.profession}
          disabled={disabled}
          autoComplete="off"
          placeholder={t("contractForm.fields.professionPlaceholder")}
          requiredIndicator
        />

        <FormLayout.Group>
          <TextField
            label={t("contractForm.fields.address")}
            value={data.issuer_address}
            onChange={handleChange("issuer_address")}
            error={errors.issuer_address}
            disabled={disabled}
            autoComplete="street-address"
            requiredIndicator
          />
          <TextField
            label={t("contractForm.fields.city")}
            value={data.issuer_city}
            onChange={handleChange("issuer_city")}
            error={errors.issuer_city}
            disabled={disabled}
            autoComplete="address-level2"
            requiredIndicator
          />
          <TextField
            label={t("contractForm.fields.postalCode")}
            value={data.issuer_postal_code}
            onChange={handleChange("issuer_postal_code")}
            error={errors.issuer_postal_code}
            disabled={disabled}
            autoComplete="postal-code"
            requiredIndicator
            maxLength={5}
          />
        </FormLayout.Group>
      </FormLayout>

      {/* Representative */}
      <Text as="h3" variant="headingMd">
        {t("contractForm.section.representative")}
      </Text>
      <FormLayout>
        <FormLayout.Group>
          <TextField
            label={t("contractForm.fields.representativeName")}
            value={data.legal_representative_name}
            onChange={handleChange("legal_representative_name")}
            error={errors.legal_representative_name}
            disabled={disabled}
            autoComplete="name"
            requiredIndicator
          />
          <TextField
            label={t("contractForm.fields.representativeVat")}
            value={data.administrator_personal_vat_id}
            onChange={handleChange("administrator_personal_vat_id")}
            error={errors.administrator_personal_vat_id}
            disabled={disabled}
            autoComplete="off"
            requiredIndicator
            maxLength={9}
          />
        </FormLayout.Group>
      </FormLayout>

      {/* Contact */}
      <Text as="h3" variant="headingMd">
        {t("contractForm.section.contact")}
      </Text>
      <FormLayout>
        <FormLayout.Group>
          <TextField
            label={t("contractForm.fields.contactEmail")}
            value={data.contact_email}
            onChange={handleChange("contact_email")}
            error={errors.contact_email}
            disabled={disabled}
            autoComplete="email"
            type="email"
            requiredIndicator
            helpText={t("contractForm.fields.contactEmailHelp")}
          />
          <TextField
            label={t("contractForm.fields.phone")}
            value={data.phone}
            onChange={handleChange("phone")}
            error={errors.phone}
            disabled={disabled}
            autoComplete="tel"
            type="tel"
          />
        </FormLayout.Group>
      </FormLayout>

      {/* Service Type */}
      <Text as="h3" variant="headingMd">
        {t("contractForm.section.serviceType")}
      </Text>
      <FormLayout>
        <Select
          label={t("contractForm.fields.serviceTypeLabel")}
          options={SERVICE_TYPE_OPTIONS}
          value={data.service_type}
          onChange={handleChange("service_type") as (value: string) => void}
          disabled={disabled}
        />

        {needsAltProvider && (
          <>
            <Text as="h3" variant="headingMd">
              {t("contractForm.section.altProvider")}
            </Text>
            <Banner tone="info">
              <p>
                <strong>{t("contractForm.fields.b2cBannerTitle")}</strong>{" "}
                {t("contractForm.fields.b2cBannerBody")}
              </p>
            </Banner>
            <FormLayout.Group>
              <TextField
                label={t("contractForm.fields.altProviderName")}
                helpText={t("contractForm.fields.altProviderNameHelp")}
                value={data.alt_provider_name}
                onChange={handleChange("alt_provider_name")}
                error={errors.alt_provider_name}
                disabled={disabled}
                autoComplete="off"
                requiredIndicator
              />
              <TextField
                label={t("contractForm.fields.altProviderNr")}
                helpText={t("contractForm.fields.altProviderNrHelp")}
                value={data.alt_provider_contract_nr}
                onChange={handleChange("alt_provider_contract_nr")}
                error={errors.alt_provider_contract_nr}
                disabled={disabled}
                autoComplete="off"
                requiredIndicator
              />
              <TextField
                label={t("contractForm.fields.altProviderDate")}
                helpText={t("contractForm.fields.altProviderDateHelp")}
                value={data.alt_provider_contract_date}
                onChange={handleChange("alt_provider_contract_date")}
                error={errors.alt_provider_contract_date}
                disabled={disabled}
                autoComplete="off"
                type="date"
                requiredIndicator
              />
            </FormLayout.Group>
          </>
        )}
      </FormLayout>
    </BlockStack>
  );
}
